
import * as modals from "@/lib/modals";
import { Component, Mixins, Prop } from "vue-property-decorator";
import mixin from "./mixin";
import NoteFields from "@/components/widgets/NoteFields.vue";
import Select from "@/components/form/Select.vue";
import { getModule } from "vuex-module-decorators";
import { ReasonCodeResource } from "../../../models/api/system/reasonCodes";
import { ScheduleDeliveryOrderModel, ReasonCodeLegendItem } from "@/models/ui/scheduleDelivery";
import Dictionary from "@/models/dictionary";
import {
  PreviewScheduleOrderRequest,
  PreviewScheduleOrderResource
} from "../../../models/api/orders/schedule";
import { faBorderNone } from "@fortawesome/free-solid-svg-icons";
import {
  GetOrdersRequest,
  GetOrdersResponse,
  OrderListItemResource
} from "../../../models/api/orders/get";
import { OrderModule, SystemModule } from "@/store/modules";
import { formatDate } from "@/utils";
import OrderDeliveryProfileWidget from "@/components/order/OrderDeliveryProfileWidget.vue";

const systemModule = getModule(SystemModule);
const orderModule = getModule(OrderModule);

@Component({
  name: modals.scheduleDeliveryModal,
  components: {
    NoteFields,
    Select,
    OrderDeliveryProfileWidget
  }
})
export default class ScheduleDeliveryModal extends Mixins(mixin) {
  loading: boolean = true;
  note: any = { text: null, visibleOption: "0", customerViewable: true, facilityViewable: true };
  deliveryDate: Date = null;
  orders: ScheduleDeliveryOrderModel[] = null;
  reasonCodesForBillTo: Dictionary<any[]> = {};
  unscheduledOrderNumbers: number[] = [];

  get deliveryForId() {
    if(!this.orders || this.orders.length == 0)
      return 0;
      
    return this.orders[0].deliveryForId ?? 0;
  }

  get orderData() {
    return orderModule.data;
  }

  get canScheduleAndAddToTruck() {
    return this.selectedItems.length === 1;
  }

  reset() {
    this.note = { text: null, visibleOption: "0", customerViewable: true, facilityViewable: true };
    this.deliveryDate = null;
    this.orders = [];
    this.reasonCodesForBillTo = {};
  }

  getReasonCodeOptions(order: ScheduleDeliveryOrderModel) {
    // Exclude "Initial Appointment" for any orders already scheduled.
    if (!order.scheduledDeliveryDate.toString().startsWith('2049-12-31')) {
      var reasonCodes = order.availableReasonCodes.slice(); // shallow copy
      var position = reasonCodes.map(x => x.label).indexOf('Initial Appointment');
      if(position > -1) reasonCodes.splice(position, 1);
      return reasonCodes;
    }

    return order.availableReasonCodes;
  }

  setReasonCodes(i) {
    if(i == 0) {
      for (let index = 0; index < this.orders.length; index++) {
        const found = this.orders[index].availableReasonCodes.find((element) => element.value == this.orders[i].reasonCode);
        if (found) {
          this.orders[index].reasonCode = this.orders[i].reasonCode;
        }
      }
    }
  }

  fixEndTime(i) {
    const time = this.orders[i]?.timeframeStart?.split(":") || 0;
    this.orders[i].timeframeEnd = (parseInt(time[0]) + 4).toString() + ":" + time[1];
  }

  fixStartTime(i) {
    const time = this.orders[i].timeframeEnd?.split(":") || 0;
    this.orders[i].timeframeStart = (parseInt(time[0]) - 4).toString() + ":" + time[1];
  }
  async load() {
    this.selectedItems?.map(x => ({ number: x.number, billToId: x.billTo?.id }));
    this.show();
    this.orders = [];

    const request = {
      orderNumbers: (this.selectedItems || []).map(x => x.number)
    };

    let result: PreviewScheduleOrderResource[] = null;
    try {
      result = await orderModule.previewScheduleOrder(request);
    } catch (ex) {}
    
    // Load delivery profile for the first order if scheduling from Dispatch
    if (this.selectedItems && this.selectedItems.length > 0) {
      try {
        await orderModule.getCurrentDeliveryProfile(this.selectedItems[0].number);
      } catch (ex) {
        console.warn("Failed to load delivery profile:", ex);
      }
    }
    
    this.loading = false;
    if (this.selectedItem) {
      for (let index = 0; index < this.selectedItems.length; index++) {
        const currentOrder = this.selectedItems[index];
        const previewOrder = result
          ? result.find(x => x.orderHeaderNumber == currentOrder.number)
          : null;

        let reasonCodes: any[] = null;

        try {
          if (currentOrder.billTo && currentOrder.billTo.id) {
            if (this.reasonCodesForBillTo[currentOrder.billTo.id]) {
              reasonCodes = this.reasonCodesForBillTo[currentOrder.billTo.id];
            } else {
              const reasonCodesResponse = await systemModule.getReasonCodesByCompany(
                currentOrder.billTo.id
              );

              if (Array.isArray(reasonCodesResponse)) {
                reasonCodes = reasonCodesResponse.map(x => ({
                  label: x.description,
                  value: x.id ? x.id : null
                }));

                let initials = reasonCodes.filter(x => x.label.includes("Initial"));
                let others = reasonCodes.filter(x => !x.label.includes("Initial"));

                reasonCodes = [...initials, ...others];
              } else {
                reasonCodes = [];
              }

              this.reasonCodesForBillTo[currentOrder.billTo.id] = reasonCodes;
            }
          } else {
            reasonCodes = [];
          }
        } catch (ex) { reasonCodes = []; }

        this.orders.push({
          headerNumber: currentOrder.number,
          crn: currentOrder.crn,
          timeframeStart: null,
          timeframeEnd: null,
          driverNote: previewOrder.driverNote,
          reasonCode: null,
          reasonText1: null,
          reasonText2: null,
          availableReasonCodes: reasonCodes ?? [],
          remarks: previewOrder.remarks,
          missingShipsWithOrderNumbers: previewOrder.missingShipsWithHeaderNumbers,
          truckInfo: previewOrder.truckInfo,
          scheduledDeliveryDate: currentOrder.scheduledDeliveryDate,
          deliveryForId: previewOrder.deliveryForId ?? 99,
          reasonCodeLegend: []
        });

        try {
          const legend = await orderModule.getSchedulingReasonCodeLegend(currentOrder.billTo?.id);
          if (legend && legend.legend && legend.legend.length > 0) {
            this.orders[this.orders.length - 1].reasonCodeLegend = legend.legend;
          }
        } catch (ex) { console.error('[Legend] load() - error:', ex); }
      }
    }
  }

  noteUpdated(newNote) {
    this.note = JSON.parse(JSON.stringify(newNote));
  }
  async schedule() {
    this.isLoading = true;

    for (let index = 0; index < this.orders.length; index++) {
      let order = this.orders[index];
      try {
        await this.executeScheduleOrder(order);
        this.addActionResult({
          number: order.headerNumber,
          success: true,
          message: `Order ${order.headerNumber} successfully scheduled`
        });
      } catch (error) {
        this.addActionResult({
          number: order.headerNumber,
          success: false,
          message: error.response.data.message,
          errors:
            error.response.data && error.response.data.errors ? error.response.data.errors : null,
          errorMessages:
            error.response.data && error.response.data.errorMessages
              ? error.response.data.errorMessages
              : null
        });
      }
    }

    await this.refreshOrders();

    this.hideModal();
  }
  executeScheduleOrder(order: ScheduleDeliveryOrderModel) {
    let request = {
      note: this.note,
      orderNumber: order.headerNumber,
      deliveryDate: this.deliveryDate,
      timeframeEnd: order.timeframeEnd,
      timeframeStart: order.timeframeStart,
      driverNote: order.driverNote,
      remarks: order.remarks,
      reasonCode: order.reasonCode,
      reasonText1: order.reasonText1,
      reasonText2: order.reasonText2
    };

    return orderModule.scheduleOrder(request);
  }

  get ordersWithMissingShipsWithOrders() {
    return this.orders.filter(
      x => x.missingShipsWithOrderNumbers && x.missingShipsWithOrderNumbers.length > 0
    );
  }

  get ordersOnTrucks() {
    return this.orders.filter(x => x.truckInfo);
  }

  get reasonCodeLegend() {
    if (!this.orders) return [];
    const orderWithLegend = this.orders.find(o => o.reasonCodeLegend && o.reasonCodeLegend.length > 0);
    return orderWithLegend ? orderWithLegend.reasonCodeLegend : [];
  }

  get canSchedule() {
    if (!this.orders) return false;
    return (
      this.orders.filter(
        x => (x.availableReasonCodes.length > 0 && !x.reasonCode) || !this.deliveryDate
      ).length < 1
    );
  }

  formatDate(date) {
    return formatDate(date);
  }

  async addOrder(headerNumber) {
    if (this.orders.filter(x => x.headerNumber == headerNumber).length > 0) return;

    this.loading = true;

    var getOrdersRequest = new GetOrdersRequest();
    getOrdersRequest.orderNumber = headerNumber;
    getOrdersRequest.pageNumber = 1;
    getOrdersRequest.pageSize = 1;

    const response = await orderModule.getOrders(getOrdersRequest);

    if (response.items && response.items.length > 0) {
      var selectedItem = response.items.map(x => ({
        number: x.headerNumber,
        crn: x.crn,
        scheduledDeliveryDate: x.scheduledDeliveryDate,
        truckName: x.truckName,
        zip3: x.consignee.address.zip.substring(0, 3),
        facilityCode: x.facility,
        billTo: { id: x.billTo ? x.billTo.id : null },
        deliveryForId: x.deliveryForId
      }))[0];

      const request = {
        orderNumbers: this.orders.map(x => x.headerNumber)
      };

      var orderDataFilter = this.orderData.filter((x: any) => x.number == headerNumber);
      if (orderDataFilter && orderDataFilter.length > 0) {
        this.addSelectedItem(selectedItem);
      }

      request.orderNumbers.push(headerNumber);

      let result: PreviewScheduleOrderResource[] = null;
      try {
        result = await orderModule.previewScheduleOrder(request);
      } catch (ex) {}
      this.loading = false;

      const currentOrder = selectedItem;
      const previewOrder = result.find(x => x.orderHeaderNumber == currentOrder.number);

      let reasonCodes: any[] = null;

      try {
        if (currentOrder.billTo && currentOrder.billTo.id) {
          if (this.reasonCodesForBillTo[currentOrder.billTo.id]) {
            reasonCodes = this.reasonCodesForBillTo[currentOrder.billTo.id];
          } else {
            const reasonCodesResponse = await systemModule.getReasonCodesByCompany(
              currentOrder.billTo.id
            );

            if (Array.isArray(reasonCodesResponse)) {
              reasonCodes = reasonCodesResponse.map(x => ({
                label: x.description,
                value: x.id ? x.id : null
              }));
            } else {
              reasonCodes = [];
            }
            this.reasonCodesForBillTo[currentOrder.billTo.id] = reasonCodes;
          }
        } else {
          reasonCodes = [];
        }
      } catch (ex) { reasonCodes = []; }

      this.orders.push({
        headerNumber: currentOrder.number,
        crn: currentOrder.crn,
        timeframeStart: null,
        timeframeEnd: null,
        driverNote: previewOrder.driverNote,
        reasonCode: null,
        reasonText1: null,
        reasonText2: null,
        availableReasonCodes: reasonCodes ?? [],
        remarks: previewOrder.remarks,
        missingShipsWithOrderNumbers: previewOrder.missingShipsWithHeaderNumbers,
        truckInfo: previewOrder.truckInfo,
        scheduledDeliveryDate: currentOrder.scheduledDeliveryDate,
        deliveryForId: currentOrder.deliveryForId,
        reasonCodeLegend: []
      });

      try {
        console.log('[Legend] addOrder() - calling with billTo.id:', currentOrder.billTo?.id);
        const legend = await orderModule.getSchedulingReasonCodeLegend(currentOrder.billTo?.id);
        console.log('[Legend] addOrder() - result:', legend);
        if (legend && legend.legend && legend.legend.length > 0) {
          this.orders[this.orders.length - 1].reasonCodeLegend = legend.legend;
        }
      } catch (ex) { console.error('[Legend] addOrder() - error:', ex); }

      var ordersWithMissingShipsWithHeaderNumbers = this.ordersWithMissingShipsWithOrders.filter(
        x => x.missingShipsWithOrderNumbers.indexOf(headerNumber) > -1
      );
      for (let index = 0; index < ordersWithMissingShipsWithHeaderNumbers.length; index++) {
        const order = ordersWithMissingShipsWithHeaderNumbers[index];
        const removeIndex = order.missingShipsWithOrderNumbers.indexOf(headerNumber);
        order.missingShipsWithOrderNumbers.splice(removeIndex, 1);
      }
    }
  }

  async addAllShipsWithOrders(headerNumber) {
    if (this.orders.filter(x => x.headerNumber == headerNumber).length < 1) return;

    var addedOrders = [];

    this.loading = true;

    var getOrdersRequest = new GetOrdersRequest();
    getOrdersRequest.shipsWithOrderNumber = headerNumber;
    getOrdersRequest.pageNumber = 1;
    getOrdersRequest.pageSize = 200;

    const response = await orderModule.getOrders(getOrdersRequest);

    if (response.items && response.items.length > 0) {
      for (var i = 0; i < response.items.length; i++) {
        var selectedItem = response.items.map(x => ({
          number: x.headerNumber,
          crn: x.crn,
          scheduledDeliveryDate: x.scheduledDeliveryDate,
          truckName: x.truckName,
          zip3: x.consignee.address.zip.substring(0, 3),
          facilityCode: x.facility,
          billTo: { id: x.billTo ? x.billTo.id : null }
        }))[i];

        if (this.orders.filter(x => x.headerNumber == selectedItem.number).length == 0) {
          addedOrders.push(selectedItem);

          var orderDataFilter = this.orderData.filter((x: any) => x.number == selectedItem.number);
          if (orderDataFilter && orderDataFilter.length > 0) {
            this.addSelectedItem(selectedItem);
          }
        }
      }

      if (addedOrders && addedOrders.length > 0) {
        const request = {
          orderNumbers: this.orders.map(x => x.headerNumber)
        };

        for (var i = 0; i < addedOrders.length; i++)
          request.orderNumbers.push(addedOrders[i].number);

        let result: PreviewScheduleOrderResource[] = null;
        try {
          result = await orderModule.previewScheduleOrder(request);
        } catch (ex) {}
        this.loading = false;

        for (var i = 0; i < addedOrders.length; i++) {
          const currentOrder = addedOrders[i];
          const previewOrder = result.find(x => x.orderHeaderNumber == currentOrder.number);

          let reasonCodes: any[] = null;

          try {
            if (currentOrder.billTo && currentOrder.billTo.id) {
              if (this.reasonCodesForBillTo[currentOrder.billTo.id]) {
                reasonCodes = this.reasonCodesForBillTo[currentOrder.billTo.id];
              } else {
                const reasonCodesResponse = await systemModule.getReasonCodesByCompany(
                  currentOrder.billTo.id
                );

                if (Array.isArray(reasonCodesResponse)) {
                  reasonCodes = reasonCodesResponse.map(x => ({
                    label: x.description,
                    value: x.id ? x.id : null
                  }));
                } else {
                  reasonCodes = [];
                }
                this.reasonCodesForBillTo[currentOrder.billTo.id] = reasonCodes;
              }
            } else {
              reasonCodes = [];
            }
          } catch (ex) { reasonCodes = []; }

          this.orders.push({
            headerNumber: currentOrder.number,
            crn: currentOrder.crn,
            timeframeStart: null,
            timeframeEnd: null,
            driverNote: previewOrder.driverNote,
            reasonCode: null,
            reasonText1: null,
            reasonText2: null,
            availableReasonCodes: reasonCodes ?? [],
            remarks: previewOrder.remarks,
            missingShipsWithOrderNumbers: previewOrder.missingShipsWithHeaderNumbers,
            truckInfo: previewOrder.truckInfo,
            scheduledDeliveryDate: currentOrder.scheduledDeliveryDate,
            deliveryForId: currentOrder.deliveryForId,
            reasonCodeLegend: []
          });

          try {
            console.log('[Legend] addAllShipsWithOrders() - calling with billTo.id:', currentOrder.billTo?.id);
            const legend = await orderModule.getSchedulingReasonCodeLegend(currentOrder.billTo?.id);
            console.log('[Legend] addAllShipsWithOrders() - result:', legend);
            if (legend && legend.legend && legend.legend.length > 0) {
              this.orders[this.orders.length - 1].reasonCodeLegend = legend.legend;
            }
          } catch (ex) { console.error('[Legend] addAllShipsWithOrders() - error:', ex); }

          var ordersWithMissingShipsWithHeaderNumbers = this.ordersWithMissingShipsWithOrders.filter(
            x => x.missingShipsWithOrderNumbers.indexOf(currentOrder.number) > -1
          );
          for (let index = 0; index < ordersWithMissingShipsWithHeaderNumbers.length; index++) {
            const order = ordersWithMissingShipsWithHeaderNumbers[index];
            const removeIndex = order.missingShipsWithOrderNumbers.indexOf(currentOrder.number);
            order.missingShipsWithOrderNumbers.splice(removeIndex, 1);
          }
        }
      }
    }
  }
}
